import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import { AdminIncidentDetail, AdminIncidentItem, AdminIncidentListPayload } from '../types';

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const truncate = (value: string, max = 96): string =>
  value.length <= max ? value : `${value.slice(0, max - 3)}...`;

const stringifyContext = (value: unknown): string => {
  if (value === null || value === undefined) return '{}';

  try {
    return truncate(JSON.stringify(value));
  } catch {
    return '{}';
  }
};

const formatTimelineEvent = (event: AdminIncidentDetail['timeline'][number]): string => {
  const parts = [event.event_type, event.actor_name || event.actor_email || event.actor_user_id || 'Sistema'];

  if (event.previous_status || event.next_status) {
    parts.push(`${event.previous_status || '-'} -> ${event.next_status || '-'}`);
  }

  if (event.next_assigned_to_name || event.next_assigned_to_user_id) {
    parts.push(`owner: ${event.next_assigned_to_name || event.next_assigned_to_user_id}`);
  }

  if (event.reason) {
    parts.push(event.reason);
  }

  parts.push(formatDateTime(event.created_at));
  return parts.join(' | ');
};

const IncidentsPage: React.FC = () => {
  const [payload, setPayload] = useState<AdminIncidentListPayload | null>(null);
  const [detail, setDetail] = useState<AdminIncidentDetail | null>(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<'assign' | 'ack' | 'resolve' | ''>('');
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [actionError, setActionError] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [ackReason, setAckReason] = useState('');
  const [resolveReason, setResolveReason] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const nextPayload = await adminService.listIncidents();
      setPayload(nextPayload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar incidents da plataforma');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (incidentId: string) => {
    setSelectedIncidentId(incidentId);
    setDetailLoading(true);
    setDetailError('');

    try {
      const nextDetail = await adminService.getIncidentDetail(incidentId);
      setDetail(nextDetail);
    } catch (nextError: any) {
      setDetail(null);
      setDetailError(nextError.message || 'Erro ao carregar o detalhe do incident');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedIncidentId && payload?.items.length) {
      void loadDetail(payload.items[0].id);
    }
  }, [loadDetail, payload, selectedIncidentId]);

  const metricCards = useMemo(
    () => [
      {
        eyebrow: 'Incidents',
        title: 'Itens no feed',
        value: String(payload?.summary.total_items || 0),
        tone: 'blue' as const,
      },
      {
        eyebrow: 'Open',
        title: 'Abertos',
        value: String(payload?.summary.open_items || 0),
        tone: 'emerald' as const,
      },
      {
        eyebrow: 'Critical',
        title: 'Criticos',
        value: String(payload?.summary.critical_open || 0),
        tone: 'rose' as const,
      },
      {
        eyebrow: 'Persistidos',
        title: 'Com workflow',
        value: String(payload?.summary.persisted_items || 0),
        tone: 'slate' as const,
      },
    ],
    [payload]
  );

  const refreshAll = async (incidentId?: string) => {
    await loadData();
    if (incidentId || selectedIncidentId) {
      await loadDetail(incidentId || selectedIncidentId);
    }
  };

  const handleAssign = async () => {
    if (!detail) return;
    const reason = assignReason.trim();
    if (!reason) {
      setActionError('Informe o motivo para atribuir este incident.');
      return;
    }

    setActionLoading('assign');
    setActionError('');

    try {
      const nextDetail = await adminService.assignIncident(detail.incident.id, { reason });
      setDetail(nextDetail);
      setSelectedIncidentId(nextDetail.incident.id);
      setAssignReason('');
      await refreshAll(nextDetail.incident.id);
    } catch (nextError: any) {
      setActionError(nextError.message || 'Erro ao atribuir incident');
    } finally {
      setActionLoading('');
    }
  };

  const handleAcknowledge = async () => {
    if (!detail) return;
    const reason = ackReason.trim();
    if (!reason) {
      setActionError('Informe o motivo para registrar o acknowledge deste incident.');
      return;
    }

    setActionLoading('ack');
    setActionError('');

    try {
      const nextDetail = await adminService.acknowledgeIncident(detail.incident.id, { reason });
      setDetail(nextDetail);
      setSelectedIncidentId(nextDetail.incident.id);
      setAckReason('');
      await refreshAll(nextDetail.incident.id);
    } catch (nextError: any) {
      setActionError(nextError.message || 'Erro ao registrar acknowledge do incident');
    } finally {
      setActionLoading('');
    }
  };

  const handleResolve = async () => {
    if (!detail) return;
    const reason = resolveReason.trim();
    if (!reason) {
      setActionError('Informe o motivo para resolver este incident.');
      return;
    }

    setActionLoading('resolve');
    setActionError('');

    try {
      const nextDetail = await adminService.resolveIncident(detail.incident.id, { reason });
      setDetail(nextDetail);
      setSelectedIncidentId(nextDetail.incident.id);
      setResolveReason('');
      await refreshAll(nextDetail.incident.id);
    } catch (nextError: any) {
      setActionError(nextError.message || 'Erro ao resolver incident');
    } finally {
      setActionLoading('');
    }
  };

  const items = payload?.items || [];
  const currentIncident: AdminIncidentItem | null = detail?.incident || null;

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Incidents</p>
          <h1>Fila operacional com workflow auditado</h1>
          <p>
            O feed agora combina incidents persistidos ou derivados com assign, acknowledge, resolve
            e correlacao reversa com a camada de security.
          </p>
        </div>
        <button
          type="button"
          className="admin-secondary-button"
          onClick={() => void refreshAll()}
          disabled={loading || detailLoading || actionLoading !== ''}
        >
          {loading || detailLoading || actionLoading !== '' ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}
      {detailError ? <div className="admin-banner admin-banner--error">{detailError}</div> : null}
      {actionError ? <div className="admin-banner admin-banner--error">{actionError}</div> : null}

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Feed</p>
              <h2>Incidents atuais da plataforma</h2>
            </div>
            <span className="admin-chip">Atualizado em {formatDateTime(payload?.generated_at)}</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Severidade</th>
                  <th>Origem</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((item: AdminIncidentItem) => (
                    <tr
                      key={item.id}
                      className={selectedIncidentId === item.id ? 'admin-table__row--selected' : ''}
                    >
                      <td>
                        <strong>{item.title}</strong>
                        <span>{item.category}</span>
                      </td>
                      <td>
                        <strong>{item.severity}</strong>
                        <span>{item.source_type}</span>
                      </td>
                      <td>
                        <strong>{item.source}</strong>
                        <span>{item.closed_at ? `Fechado: ${formatDateTime(item.closed_at)}` : formatDateTime(item.opened_at)}</span>
                      </td>
                      <td>
                        <strong>{item.status}</strong>
                        <span>{item.acknowledged_at ? `Ack: ${formatDateTime(item.acknowledged_at)}` : 'Sem ack'}</span>
                      </td>
                      <td>
                        <strong>{item.assigned_to_name || 'Sem owner'}</strong>
                        <span>{item.resolved_by_name || '-'}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-link-chip admin-link-chip--button"
                          onClick={() => void loadDetail(item.id)}
                          disabled={detailLoading && selectedIncidentId === item.id}
                        >
                          {detailLoading && selectedIncidentId === item.id ? 'Abrindo...' : 'Abrir'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Nenhum incident encontrado ainda no feed consolidado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Detalhe</p>
              <h2>{currentIncident?.title || 'Selecione um incident'}</h2>
            </div>
            <span className="admin-chip">{currentIncident?.status || 'Sem foco ativo'}</span>
          </div>

          {currentIncident ? (
            <>
              <div className="admin-kpi-grid">
                <article className="admin-kpi-item"><span>Severidade</span><strong>{currentIncident.severity}</strong></article>
                <article className="admin-kpi-item"><span>Origem</span><strong>{currentIncident.source_type}</strong></article>
                <article className="admin-kpi-item"><span>Owner</span><strong>{currentIncident.assigned_to_name || 'Sem owner'}</strong></article>
                <article className="admin-kpi-item"><span>Acknowledge</span><strong>{currentIncident.acknowledged_at ? 'SIM' : 'NAO'}</strong></article>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair"><span>Categoria</span><strong>{currentIncident.category}</strong></div>
                <div className="admin-detail-pair"><span>Fonte</span><strong>{currentIncident.source}</strong></div>
                <div className="admin-detail-pair"><span>Aberto</span><strong>{formatDateTime(currentIncident.opened_at)}</strong></div>
                <div className="admin-detail-pair"><span>Fechado</span><strong>{formatDateTime(currentIncident.closed_at)}</strong></div>
                <div className="admin-detail-pair"><span>Acknowledged por</span><strong>{currentIncident.acknowledged_by_name || '-'}</strong></div>
                <div className="admin-detail-pair"><span>Resolvido por</span><strong>{currentIncident.resolved_by_name || '-'}</strong></div>
              </div>

              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Contexto</p>
                  <h2>Payload operacional</h2>
                </div>
                {detail?.actions.promote_on_action ? <span className="admin-chip">Vai promover derivado</span> : null}
              </div>
              <div className="admin-note-chip">{stringifyContext(currentIncident.context_json)}</div>

              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Security</p>
                  <h2>Correlacao reversa</h2>
                </div>
                <span className="admin-chip">
                  {detail?.linked_security_alert ? detail.linked_security_alert.status : 'Sem link'}
                </span>
              </div>
              {detail?.linked_security_alert ? (
                <div className="admin-detail-grid">
                  <div className="admin-detail-pair"><span>Alerta</span><strong>{detail.linked_security_alert.title}</strong></div>
                  <div className="admin-detail-pair"><span>Severidade</span><strong>{detail.linked_security_alert.severity}</strong></div>
                  <div className="admin-detail-pair"><span>Target</span><strong>{detail.linked_security_alert.target_type}</strong></div>
                  <div className="admin-detail-pair"><span>Entidade</span><strong>{detail.linked_security_alert.target_label}</strong></div>
                  <div className="admin-detail-pair"><span>Bundle</span><strong>{detail.linked_security_alert.bundle_available ? 'DISPONIVEL' : 'NAO'}</strong></div>
                  <div className="admin-detail-pair"><span>Aberto</span><strong>{formatDateTime(detail.linked_security_alert.opened_at)}</strong></div>
                </div>
              ) : (
                <div className="admin-note-chip">Este incident nao possui alerta de security correlacionado no momento.</div>
              )}
            </>
          ) : (
            <div className="admin-note-chip">{detailLoading ? 'Carregando incident...' : 'Selecione um item do feed para abrir o detalhe.'}</div>
          )}
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Workflow</p>
              <h2>Assign, acknowledge e resolve</h2>
            </div>
          </div>

          {detail ? (
            <>
              <div className="admin-filter-grid">
                <label className="admin-filter-field">
                  <span>Motivo para assign</span>
                  <textarea
                    className="admin-search-input"
                    rows={3}
                    value={assignReason}
                    onChange={(event) => setAssignReason(event.target.value)}
                    placeholder="Explique por que este incident precisa de ownership."
                  />
                </label>
                <label className="admin-filter-field">
                  <span>Motivo para acknowledge</span>
                  <textarea
                    className="admin-search-input"
                    rows={3}
                    value={ackReason}
                    onChange={(event) => setAckReason(event.target.value)}
                    placeholder="Documente o reconhecimento operacional deste incident."
                  />
                </label>
                <label className="admin-filter-field">
                  <span>Motivo para resolve</span>
                  <textarea
                    className="admin-search-input"
                    rows={3}
                    value={resolveReason}
                    onChange={(event) => setResolveReason(event.target.value)}
                    placeholder="Explique por que o incident pode ser encerrado."
                  />
                </label>
              </div>

              <div className="admin-filter-actions">
                <button
                  type="button"
                  className="admin-secondary-button"
                  onClick={() => void handleAssign()}
                  disabled={!detail.actions.can_assign || actionLoading === 'assign'}
                >
                  {actionLoading === 'assign' ? 'Atribuindo...' : 'Assign'}
                </button>
                <button
                  type="button"
                  className="admin-secondary-button"
                  onClick={() => void handleAcknowledge()}
                  disabled={!detail.actions.can_acknowledge || actionLoading === 'ack'}
                >
                  {actionLoading === 'ack' ? 'Registrando...' : 'Acknowledge'}
                </button>
                <button
                  type="button"
                  className="admin-secondary-button"
                  onClick={() => void handleResolve()}
                  disabled={!detail.actions.can_resolve || actionLoading === 'resolve'}
                >
                  {actionLoading === 'resolve' ? 'Resolvendo...' : 'Resolve'}
                </button>
              </div>
            </>
          ) : (
            <div className="admin-note-chip">Selecione um incident antes de usar o workflow.</div>
          )}
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Timeline</p>
              <h2>Eventos do incident</h2>
            </div>
            <span className="admin-chip">{detail?.timeline.length || 0} evento(s)</span>
          </div>
          <div className="admin-note-stack">
            {detail?.timeline.length ? (
              detail.timeline.map((event) => (
                <div key={event.id} className="admin-note-chip">
                  {formatTimelineEvent(event)}
                </div>
              ))
            ) : (
              <div className="admin-note-chip">
                {detailLoading ? 'Carregando timeline...' : 'Nenhum evento foi registrado ainda para este incident.'}
              </div>
            )}
          </div>
        </article>
      </section>

      {payload ? (
        <section className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Notas</p>
              <h2>Limites desta etapa</h2>
            </div>
          </div>

          <div className="admin-note-stack">
            {payload.notes.map((note) => (
              <div key={note} className="admin-note-chip">
                {note}
              </div>
            ))}
            {(detail?.notes || []).map((note, index) => (
              <div key={`detail-note-${index}`} className="admin-note-chip">
                {note}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default IncidentsPage;
